import { client } from '@/api/orpc'
import { debounce } from '@/utils/debounce'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useLayout } from './layout'
import { useUser } from './user'
import { useInventory } from './inventory'

export const useMarket = defineStore('market', () => {
	const initLoading = ref(true)
	const loading = ref(false)
	let fetchingMore = false
	const search = ref('')
	const pages = ref(0)
	const page = ref(1)
	const items = ref<
		{
			uuid: string
			name: string
			collectionName: string
			preloader: string | null
			coins: number
		}[]
	>([])
	const selected = ref<
		{
			uuid: string
			name: string
			collectionName: string
			preloader: string | null
			coins: number
		}[]
	>([])
	const sortExpensive = ref(true)
	const minPrice = ref<number>()
	const maxPrice = ref<number>()

	const collections = ref<
		{
			uuid: string
			name: string
			preloader: string | null
			listings: number
			floorCoins: number
		}[]
	>([])
	const selectedCollections = ref<string[]>([])

	const getMarketCollections = async () => {
		const { error, data } = await client.market.getMarketCollections()

		if (error) {
			console.error(error)
			return
		}
		if (!data) {
			return
		}

		collections.value = data
	}

	getMarketCollections()

	const getMarketItems = async () => {
		const { error, data } = await client.market.getMarketItems({
			search: search.value || undefined,
			sort: sortExpensive.value ? 'DESC' : 'ASC',
			price:
				minPrice.value || maxPrice.value
					? {
							from: minPrice.value ? Math.floor(minPrice.value * 100) : 0,
							to: maxPrice.value ? Math.floor(maxPrice.value * 100) : undefined
						}
					: undefined,
			page: page.value,
			collectionNames: selectedCollections.value.length
				? selectedCollections.value
				: undefined
		})

		if (error) {
			console.error(error)
			return undefined
		}

		return data
	}

	const filterChanged = async () => {
		if (loading.value) {
			return
		}

		loading.value = true

		page.value = 1

		const data = await getMarketItems()

		if (data) {
			items.value = data.items
			pages.value = data.pages
		}

		loading.value = false
		initLoading.value = false
	}

	const debouncedFilterChanged = debounce(filterChanged, 500)

	filterChanged()

	const fetchMore = async () => {
		if (fetchingMore) {
			return
		}
		if (page.value >= pages.value) {
			return
		}

		fetchingMore = true

		page.value++
		const data = await getMarketItems()

		if (data) {
			items.value.push(...data.items)
			pages.value = data.pages
		}

		fetchingMore = false
	}

	const debouncedFetchMore = debounce(fetchMore, 500)

	const layout = useLayout()
	const user = useUser()
	const inventory = useInventory()

	const purchase = async (items: { uuid: string; coins: number }[]) => {
		if (selected.value.length === 0) {
			return { success: false }
		}

		const { error, data, isDefined } = await client.market.buyMarketItems(items)

		if (isDefined) {
			if (error.code === 'NOT_ENOUGH_COINS') {
				layout.showDepositModal()
			}
			if (error.code === 'WAGER_REQUIRED') {
				return { success: false, wagerRemaining: error.data.wagerRemaining }
			}

			return { success: false }
		}

		if (error) {
			console.error(error)
			return { success: false }
		}

		if (!data) {
			return { success: false }
		}

		user.setBalance(data.updatedUser.coins)
		inventory.pushItems(
			data.items.map((item) => ({ ...item, claimable: true }))
		)

		return { success: true }
	}

	const toggleSort = () => {
		sortExpensive.value = !sortExpensive.value
	}

	const amount = computed(() =>
		selected.value.reduce((acc, item) => acc + item.coins, 0)
	)

	const removeSelection = (uuid: string) => {
		selected.value = selected.value.filter((i) => i.uuid !== uuid)
	}

	const toggleSelection = (item: {
		uuid: string
		name: string
		collectionName: string
		preloader: string | null
		coins: number
	}) => {
		if (selected.value.find((i) => i.uuid === item.uuid)) {
			removeSelection(item.uuid)
		} else {
			selected.value.push(item)
		}
	}

	const clearSelection = () => {
		selected.value = []
	}

	const reset = () => {
		minPrice.value = undefined
		maxPrice.value = undefined
		search.value = ''
	}

	const toggleCollectionSelection = (name: string) => {
		if (selectedCollections.value.find((i) => i === name)) {
			selectedCollections.value = selectedCollections.value.filter(
				(i) => i !== name
			)
		} else {
			selectedCollections.value = [...selectedCollections.value, name]
		}
	}

	watch([minPrice, maxPrice, search, sortExpensive, selectedCollections], () =>
		debouncedFilterChanged()
	)

	return {
		loading,
		initLoading,
		collections,
		selectedCollections,
		toggleCollectionSelection,
		search,
		items,
		sortExpensive,
		minPrice,
		maxPrice,
		toggleSort,
		selected,
		clearSelection,
		amount,
		toggleSelection,
		removeSelection,
		reset,
		debouncedFetchMore,
		purchase,
		page,
		pages
	}
})
